import { CommandQueue } from './command-queue';
import { EditOp, MultiOp, deserializeEditOp } from './edit-ops';
import { Events } from './events';
import { Scene } from './scene';
import { Splat } from './splat';

// Check if an operation references a specific splat
const opReferencesSplat = (op: EditOp, splat: Splat): boolean => {
    // Handle MultiOp by checking nested operations
    if (op instanceof MultiOp) {
        return op.ops.some(nestedOp => opReferencesSplat(nestedOp, splat));
    }
    // Check for splat property on the operation
    return (op as any).splat === splat;
};

// Spatial operations are recorded to LodEditLog for cross-LOD replay.
// Non-spatial ops (color, rename, group) only affect the current LOD.
const SPATIAL_OP_NAMES = new Set([
    'selectOp', 'deleteSelection', 'pagedDelete', 'splatsTransform',
    'selectAll', 'selectNone', 'selectInvert', 'replaceSelection'
]);

const isSpatialOp = (op: EditOp): boolean => {
    if (op instanceof MultiOp) {
        return op.ops.some(nested => isSpatialOp(nested));
    }
    return SPATIAL_OP_NAMES.has(op.name);
};

// Collect all splats referenced by an EditOp (recursing into MultiOp).
const collectReferencedSplats = (op: EditOp, out: Set<Splat>): void => {
    if (op instanceof MultiOp) {
        op.ops.forEach(nested => collectReferencedSplats(nested, out));
    } else if ((op as any).splat) {
        out.add((op as any).splat as Splat);
    }
};

class EditHistory {
    history: EditOp[] = [];
    cursor = 0;
    events: Events;

    // shared queue used to serialize every history mutation. the same physical
    // CommandQueue is shared with DataProcessor callers via scene.commandQueue
    // and the 'queue' event, so all async splat work applies in initiation order.
    private commandQueue: CommandQueue;

    constructor(events: Events, commandQueue: CommandQueue) {
        this.events = events;
        this.commandQueue = commandQueue;

        events.on('edit.undo', () => this.undo());
        events.on('edit.redo', () => this.redo());
        events.on('edit.add', (editOp: EditOp, suppressOp = false) => this.add(editOp, suppressOp));
    }

    private queue<T>(fn: () => T | Promise<T>): Promise<T> {
        return this.commandQueue.enqueue(fn);
    }

    add(editOp: EditOp, suppressOp = false) {
        return this.queue(() => this._add(editOp, suppressOp));
    }

    canUndo() {
        return this.cursor > 0;
    }

    canRedo() {
        return this.cursor < this.history.length;
    }

    undo() {
        return this.queue(async () => {
            if (this.canUndo()) {
                await this._undo();
            }
        });
    }

    redo(suppressOp = false) {
        return this.queue(async () => {
            if (this.canRedo()) {
                await this._redo(suppressOp);
            }
        });
    }

    private async _add(editOp: EditOp, suppressOp = false) {
        while (this.cursor < this.history.length) {
            this.history.pop().destroy?.();
        }
        this.history.push(editOp);
        await this._redo(suppressOp);
    }

    private async _undo() {
        // only advance the cursor after a successful undo so a thrown editOp leaves
        // history in a consistent state for subsequent undo/redo.
        const editOp = this.history[this.cursor - 1];
        await editOp.undo();
        this.cursor--;
        // Sync LodEditLog cursor for spatial ops (cross-LOD replay state).
        if (isSpatialOp(editOp)) {
            const splats = new Set<Splat>();
            collectReferencedSplats(editOp, splats);
            splats.forEach(s => s.lodEditLog?.onEditHistoryUndo());
        }
        this.events.fire('edit.apply', editOp);
        this.fireEvents();
    }

    private async _redo(suppressOp = false) {
        // only advance the cursor after a successful redo so a thrown editOp leaves
        // history in a consistent state for subsequent undo/redo.
        const editOp = this.history[this.cursor];
        if (!suppressOp) {
            await editOp.do();
        }
        this.cursor++;
        // Sync LodEditLog cursor for spatial ops (cross-LOD replay state).
        if (isSpatialOp(editOp)) {
            const splats = new Set<Splat>();
            collectReferencedSplats(editOp, splats);
            splats.forEach(s => s.lodEditLog?.onEditHistoryRedo());
        }
        this.events.fire('edit.apply', editOp);
        this.fireEvents();
    }

    fireEvents() {
        this.events.fire('edit.canUndo', this.canUndo());
        this.events.fire('edit.canRedo', this.canRedo());
    }

    clear() {
        // route through the queue so any in-flight add/undo/redo finishes before we wipe
        // history, preventing queued ops from running against a cleared state.
        return this.queue(() => {
            this.history.forEach((editOp) => {
                editOp.destroy?.();
            });
            this.history = [];
            this.cursor = 0;
            this.fireEvents();
        });
    }

    // Remove all operations that reference a specific splat
    removeForSplat(splat: Splat) {
        // serialize with the queue so we don't reshape history while a queued op is mid-flight
        // (which could leave queued undo/redo pointing at indices that no longer exist).
        return this.queue(() => {
            let newCursor = 0;
            const newHistory: EditOp[] = [];

            for (let i = 0; i < this.history.length; i++) {
                const op = this.history[i];
                // Skip ops referencing the splat; don't destroy them since the caller handles that
                if (!opReferencesSplat(op, splat)) {
                    // Keep this operation
                    newHistory.push(op);
                    // Track cursor position (count kept operations before original cursor)
                    if (i < this.cursor) {
                        newCursor++;
                    }
                }
            }

            this.history = newHistory;
            this.cursor = newCursor;
            this.fireEvents();
        });
    }

    serialize() {
        return {
            version: '1.1.0',
            cursor: this.cursor,
            history: this.history.map(op => op.serialize())
        };
    }

    deserialize(data: any, scene: Scene) {
        return this.queue(async () => {
            this.history.forEach((editOp) => {
                editOp.destroy?.();
            });
            const ops: EditOp[] = [];
            for (const opData of data.history) {
                try {
                    const op = deserializeEditOp(opData, scene, this.events);
                    if (op) {
                        ops.push(op);
                    }
                } catch (e) {
                    console.warn(`Failed to deserialize EditOp '${opData.type}': ${e.message}`);
                }
            }
            this.history = ops;
            this.cursor = Math.min(data.cursor, ops.length);

            // Structural subdivision operations append rows that are intentionally
            // retained in project PLY data. Since editor state is not stored in
            // that PLY, restore the unapplied suffix from newest to oldest so
            // an undone parent subdivision also keeps all descendants hidden.
            for (let i = this.history.length - 1; i >= this.cursor; --i) {
                try {
                    await this.history[i].restoreUnapplied?.();
                } catch (e) {
                    console.warn(`Failed to restore unapplied EditOp[${i}]: ${e.message}`);
                }
            }

            // Replay all applied operations to restore splat state
            // (needed because SOG/PLY data may not include state changes)
            for (let i = 0; i < this.cursor; i++) {
                try {
                    await this.history[i].do();
                } catch (e) {
                    console.warn(`Failed to replay EditOp[${i}]: ${e.message}`);
                }
            }

            this.fireEvents();
        });
    }
}

export { EditHistory };
